import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ContextService } from '../services/context.service';
import { DatabaseService } from '../services/db.service';
import { ThemeService } from '../services/theme.service';
import { FormsModule } from '@angular/forms';
import { ToastService } from '../services/toast.service';
import { LoadingService } from '../services/loading.service';
import { SessionSecurityService } from '../services/session-security.service';
import { AuditService } from '../services/audit.service';
import { SecurityUtil } from '../utils/security.util';
import { LEGAL_TEXTS } from '../constants/legal-content';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-login',
    standalone: true,
    imports: [CommonModule, FormsModule],
    providers: [DatePipe],
    template: `
    <div class="min-h-screen flex w-full bg-transparent font-sans text-slate-900 dark:text-slate-100 selection:bg-brand-accent selection:text-white relative overflow-hidden">

        <!-- Left Side: Form Container Container -->
        <div class="w-full lg:w-1/2 flex flex-col relative overflow-hidden">
            
            <!-- Painel Principal Isolar Layer: Removido Backdrop Blur para sanar problema específico do Chrome no Windows -->
            <div class="absolute inset-0 bg-white dark:bg-slate-900" style="contain: strict;"></div>
            <div class="absolute inset-0 bg-gradient-to-tr from-brand-main/[0.04] to-brand-accent/[0.04] dark:from-brand-main/[0.08] dark:to-transparent pointer-events-none"></div>
            
            <!-- Theme Toggle Button -->
            <button (click)="theme.toggleDarkMode()" class="absolute top-6 right-6 z-50 p-2 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors shadow-sm">
                @if (theme.isDarkMode()) {
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
                } @else {
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path></svg>
                }
            </button>

            <div class="flex-1 flex flex-col justify-center px-8 sm:px-16 md:px-24 max-w-2xl mx-auto w-full relative z-10 py-12" style="contain: content;">
                
                <!-- Mobile Logo -->
                <div class="lg:hidden mb-10 flex items-center gap-4">
                    <div class="w-14 h-14 bg-white dark:bg-slate-700/50 rounded-2xl flex items-center justify-center shadow-soft border border-slate-100 dark:border-slate-700 p-2.5">
                        <img src="assets/images/logo-icon.svg" title="FilaSaúde" alt="Logo FilaSaúde" class="w-full h-full object-contain" style="width: 56px; height: 56px">
                    </div>
                    <h1 class="text-3xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100">
                        Fila<span class="text-brand-main">Saúde</span>
                    </h1>
                </div>

                <!-- Header Section -->
                <div class="mb-10">
                    <h2 class="text-3xl font-extrabold text-slate-800 dark:text-slate-100 tracking-tight mb-2">
                        Plataforma de <span class="text-brand-accent">Regulação</span>
                    </h2>
                    <p class="text-slate-500 dark:text-slate-400 font-medium text-base">
                        Acesso ao ambiente de gestão e operação.
                    </p>
                </div>

                <!-- Login Form -->
                @if (sessionSecurity.isLockedOut()) {
                    <div class="mb-6 p-4 rounded-2xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/60 text-red-700 dark:text-red-300 flex items-center gap-3 animate-pulse">
                        <svg class="w-6 h-6 shrink-0 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <div class="text-xs text-left leading-relaxed">
                            <strong class="block font-black text-sm text-red-800 dark:text-red-200">Terminal Bloqueado por Segurança</strong>
                            Múltiplas tentativas inválidas. Aguarde <span class="font-black font-mono text-sm text-red-600 dark:text-red-400">{{ sessionSecurity.lockoutRemainingSeconds() }}s</span> para tentar novamente.
                        </div>
                    </div>
                }

                <form (ngSubmit)="handleLoginWithCredentials()" class="flex flex-col">
                    <div class="space-y-6 mb-8">
                        <!-- Login Input -->
                        <div class="space-y-2 group">
                            <label class="block text-sm font-semibold text-slate-600 dark:text-slate-300 transition-colors group-focus-within:text-brand-main dark:group-focus-within:text-brand-accent">
                                Usuário / Login
                            </label>
                            <div class="relative flex items-center">
                                <div class="absolute left-4 text-slate-400 group-focus-within:text-brand-accent transition-colors">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                                </div>
                                <input [(ngModel)]="loginInput" name="login" type="text" placeholder="Digite seu login"
                                    (focus)="context.isUserTyping.set(true)"
                                    (blur)="context.isUserTyping.set(false)"
                                    class="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:bg-white dark:focus:bg-slate-800 focus:border-brand-accent focus:ring-4 focus:ring-brand-accent/10 dark:focus:ring-brand-accent/20 outline-none transition-colors duration-300">
                            </div>
                        </div>

                        <!-- Password Input -->
                        <div class="space-y-2 group">
                            <label class="block text-sm font-semibold text-slate-600 dark:text-slate-300 transition-colors group-focus-within:text-brand-main dark:group-focus-within:text-brand-accent">
                                Senha de Acesso
                            </label>
                            <div class="relative flex items-center">
                                <div class="absolute left-4 text-slate-400 group-focus-within:text-brand-accent transition-colors pointer-events-none">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                </div>
                                <input [(ngModel)]="passwordInput" name="password" [type]="showPassword() ? 'text' : 'password'" placeholder="Digite sua senha"
                                    (focus)="context.isUserTyping.set(true)"
                                    (blur)="context.isUserTyping.set(false)"
                                    class="w-full pl-12 pr-12 py-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:bg-white dark:focus:bg-slate-800 focus:border-brand-accent focus:ring-4 focus:ring-brand-accent/10 dark:focus:ring-brand-accent/20 outline-none transition-colors duration-300">
                                <button type="button" 
                                    (click)="showPassword.set(!showPassword())" 
                                    class="absolute right-4 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 focus:outline-none transition-colors"
                                    [attr.aria-label]="showPassword() ? 'Ocultar senha' : 'Exibir senha'"
                                    [title]="showPassword() ? 'Ocultar senha' : 'Exibir senha'">
                                    @if (showPassword()) {
                                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18"/>
                                        </svg>
                                    } @else {
                                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                        </svg>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Submit Button -->
                    <button type="submit" 
                        [disabled]="!loginInput || !passwordInput || isLoggingIn() || sessionSecurity.isLockedOut()"
                        class="w-full bg-brand-main hover:bg-primary-500 disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-800 dark:disabled:text-slate-500 disabled:shadow-none text-white font-bold py-4 rounded-xl shadow-[0_8px_20px_-6px_rgba(15,76,129,0.5)] hover:shadow-[0_12px_24px_-8px_rgba(14,165,233,0.6)] hover:-translate-y-0.5 disabled:transform-none disabled:cursor-not-allowed transition-colors duration-300 mt-2 mb-4 relative overflow-hidden group">
                        <span class="relative z-10 flex items-center justify-center gap-2 text-base">
                            @if (isLoggingIn()) {
                                <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Entrando no Sistema...
                            } @else {
                                Entrar no Sistema
                                <svg class="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                            }
                        </span>
                        <div class="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 pointer-events-none"></div>
                    </button>

                    <!-- Termos LGPD Checkbox -->
                    <div class="flex items-start gap-3 mb-6">
                        <input type="checkbox" [(ngModel)]="acceptedTerms" name="acceptedTerms" id="acceptedTerms"
                            class="mt-1 w-4 h-4 rounded border-slate-300 text-brand-accent focus:ring-brand-accent">
                        <label for="acceptedTerms" class="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                            Li e aceito os <button type="button" (click)="showLegalModal.set(true); currentLegalSection.set('LGPD_AUDIT_CLAUSE')" class="text-brand-accent hover:underline font-medium">Termos de Consentimento LGPD</button> 
                            para o sistema de auditoria. O acesso implica consentimento expresso com as cláusulas de auditoria e conformidade com a LGPD.
                        </label>
                    </div>
                </form>

                <div class="relative flex items-center justify-center my-6">
                    <div class="absolute inset-0 flex items-center">
                        <div class="w-full border-t border-slate-200 dark:border-slate-700"></div>
                    </div>
                    <div class="relative bg-white dark:bg-slate-800 px-4 text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                        Acesso Cidadão
                    </div>
                </div>

                <!-- Public Access Button -->
                <button id="btn-buscar-atendimento" (click)="context.isPublicStandalone.set(true)" 
                    class="w-full group relative flex items-center justify-center gap-3 bg-white dark:bg-slate-800 border-2 border-brand-accent/20 hover:border-brand-accent dark:hover:border-brand-accent text-brand-accent font-bold py-4 rounded-xl hover:bg-brand-accent hover:text-white dark:hover:bg-brand-accent dark:hover:text-white hover:shadow-lg transition-colors duration-300 overflow-hidden text-base">
                    <div class="w-8 h-8 rounded-lg bg-brand-accent/10 flex items-center justify-center group-hover:bg-white/20 transition-colors duration-300">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                    </div>
                    Consulta Pública de Transparência
                </button>

            </div>
            
            <!-- Footer -->
            <div class="p-8 text-center text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                Sistema Integrado de Regulação em Saúde
            </div>
        </div>

        <!-- Legal Modal - Outside containers to cover full screen -->
        @if (showLegalModal()) {
            <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" (click)="showLegalModal.set(false)">
                <div class="bg-white dark:bg-slate-800 rounded-3xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" (click)="$event.stopPropagation()">
                    <div class="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
                        <h3 class="text-xl font-bold text-slate-800 dark:text-slate-100">Documentos Legais</h3>
                        <button (click)="showLegalModal.set(false)" class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                            <svg class="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                    </div>
                    <div class="flex border-b border-slate-200 dark:border-slate-700 bg-slate-100/70 dark:bg-slate-900/60 p-1.5 gap-1.5">
                        <button (click)="currentLegalSection.set('LGPD_AUDIT_CLAUSE')" 
                                [ngClass]="currentLegalSection() === 'LGPD_AUDIT_CLAUSE' 
                                    ? 'bg-slate-900 text-white dark:bg-emerald-600 dark:text-white shadow-sm font-black' 
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-800/80 font-bold'" 
                                class="flex-1 py-2.5 text-xs rounded-xl transition-all cursor-pointer">
                            Consentimento LGPD
                        </button>
                        <button (click)="currentLegalSection.set('PRIVACY_POLICY')" 
                                [ngClass]="currentLegalSection() === 'PRIVACY_POLICY' 
                                    ? 'bg-slate-900 text-white dark:bg-emerald-600 dark:text-white shadow-sm font-black' 
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-800/80 font-bold'" 
                                class="flex-1 py-2.5 text-xs rounded-xl transition-all cursor-pointer">
                            Privacidade
                        </button>
                        <button (click)="currentLegalSection.set('DATA_RETENTION')" 
                                [ngClass]="currentLegalSection() === 'DATA_RETENTION' 
                                    ? 'bg-slate-900 text-white dark:bg-emerald-600 dark:text-white shadow-sm font-black' 
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-800/80 font-bold'" 
                                class="flex-1 py-2.5 text-xs rounded-xl transition-all cursor-pointer">
                            Retenção
                        </button>
                        <button (click)="currentLegalSection.set('TERMS_OF_USE')" 
                                [ngClass]="currentLegalSection() === 'TERMS_OF_USE' 
                                    ? 'bg-slate-900 text-white dark:bg-emerald-600 dark:text-white shadow-sm font-black' 
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-800/80 font-bold'" 
                                class="flex-1 py-2.5 text-xs rounded-xl transition-all cursor-pointer">
                            Termos
                        </button>
                    </div>
                    <div class="flex-1 overflow-y-auto p-6 custom-scrollbar-dark">
                        <div [innerHTML]="legalTexts[currentLegalSection()]"></div>
                    </div>
                    <div class="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
                        <button (click)="showLegalModal.set(false)" class="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black py-3 rounded-xl shadow-md transition-all active:scale-[0.99] cursor-pointer">
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        }
        
        <!-- Right Side: Brand/Image Area -->
        <div class="hidden lg:flex w-1/2 relative bg-brand-main overflow-hidden items-center justify-center" style="contain: strict;">
            <!-- Background Gradients -->
            <div class="absolute inset-0 bg-gradient-to-br from-[#0a3153] via-brand-main to-brand-accent/90"></div>
            
            <!-- Optimized Glowing Blobs -->
            <div class="absolute bottom-1/4 -right-20 w-[500px] h-[500px] rounded-full opacity-30 mix-blend-normal" style="background: radial-gradient(circle at center, #42A5F5 0%, transparent 70%);"></div>

            <!-- Dot pattern overlay -->
            <div class="absolute inset-0" style="background-image: radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px); background-size: 32px 32px;"></div>

            <!-- Content Area -->
            <div class="relative z-10 w-full flex flex-col items-center justify-center p-12 text-center">
                
                <!-- System Logo / Ripple Containers (Gota d'água) -->
                <div class="w-40 h-40 bg-white rounded-full flex items-center justify-center mb-10 shadow-[0_0_40px_rgba(255,255,255,0.2)] relative">
                    
                    <!-- Ripple Effect (Water Drop) - 4 camadas de onda com fading progressivo -->
                    <div class="absolute inset-0 rounded-full border border-white/60 animate-ripple"></div>
                    <div class="absolute inset-0 rounded-full border border-white/40 animate-ripple" style="animation-delay: 0.75s"></div>
                    <div class="absolute inset-0 rounded-full border border-white/20 animate-ripple" style="animation-delay: 1.5s"></div>
                    <div class="absolute inset-0 rounded-full border border-white/10 animate-ripple" style="animation-delay: 2.25s"></div>
                    
                    <img src="assets/images/logo-icon.svg" title="FilaSaúde" alt="Logo FilaSaúde" class="w-24 h-24 object-contain relative z-20" style="width: 96px; height: 96px; will-change: transform;">
                </div>

                <h2 class="text-3xl font-light text-white/90 mb-2 tracking-wide text-center">
                    Sistema Integrado
                </h2>
                <h3 class="text-6xl font-extrabold text-white tracking-tight drop-shadow-lg mb-8 text-center text-rendering-optimizeLegibility">
                    Fila<span class="text-brand-accent">Saúde</span>
                </h3>

                <div class="flex items-center gap-4 text-white/70 text-xs font-bold uppercase tracking-widest mt-4">
                    <span class="w-16 h-[1px] bg-white/30"></span>
                    Portal de Regulação
                    <span class="w-16 h-[1px] bg-white/30"></span>
                </div>
                
            </div>
        </div>
    </div>
    `
})
export class LoginComponent {
    context = inject(ContextService);
    db = inject(DatabaseService);
    theme = inject(ThemeService);
    toast = inject(ToastService);
    loading = inject(LoadingService);
    sessionSecurity = inject(SessionSecurityService);
    audit = inject(AuditService);
    legalTexts = LEGAL_TEXTS;

    loginInput = '';
    passwordInput = '';
    showPassword = signal(false);
    acceptedTerms = false;
    acceptedTermsDate: Date | null = null;
    showLegalModal = signal(false);
    currentLegalSection = signal<'PRIVACY_POLICY' | 'LGPD_AUDIT_CLAUSE' | 'DATA_RETENTION' | 'TERMS_OF_USE'>('LGPD_AUDIT_CLAUSE');
    isLoggingIn = signal(false);

    constructor() {
        // Handle direct links
        const params = new URLSearchParams(window.location.search);
        if (params.get('portal') === 'public' || params.get('portal') === 'standalone') {
            this.context.isPublicStandalone.set(true);
        } else if (params.get('busca') === 'true' || window.location.hash === '#buscar') {
            setTimeout(() => {
                document.getElementById('btn-buscar-atendimento')?.scrollIntoView({ behavior: 'smooth' });
            }, 500);
        }
    }

    async handleLoginWithCredentials() {
        if (!this.loginInput || !this.passwordInput) {
            return;
        }

        // 1. Verificação de Força Bruta
        const canAttempt = this.sessionSecurity.canAttemptLogin();
        if (!canAttempt.allowed) {
            this.toast.show(`Acesso temporariamente bloqueado. Aguarde ${canAttempt.remainingSeconds}s para tentar novamente.`, 'error');
            return;
        }

        // 2. Proteção contra SQL Injection e caracteres perigosos
        if (SecurityUtil.isPotentialSqlInjection(this.loginInput) || SecurityUtil.isPotentialSqlInjection(this.passwordInput)) {
            this.passwordInput = '';
            this.showPassword.set(false);
            this.audit.logAction(
                'SEGURANCA_TENTATIVA_INJECAO_SQL',
                'Tentativa de injeção SQL neutralizada no formulário de login',
                {
                    loginAttempted: this.loginInput,
                    timestamp: new Date().toISOString()
                },
                this.loginInput || 'ANÔNIMO'
            ).catch(err => console.error('[Login] Erro ao auditar injeção SQL:', err));

            this.toast.show('Padrão inválido ou caracteres maliciosos detectados. Ação registrada para auditoria.', 'error');
            return;
        }
        
        if (!this.acceptedTerms) {
            this.toast.show('É necessário aceitar os Termos de Consentimento LGPD para acessar o sistema.', 'warning');
            return;
        }
        
        this.isLoggingIn.set(true);
        try {
            // Registrar aceite dos termos antes de fazer login
            this.acceptedTermsDate = new Date();
            
            const res = await this.db.authenticateOperator(this.loginInput, this.passwordInput);

            // Sucesso: reseta histórico de falhas e inicia contagem de inatividade
            this.sessionSecurity.recordSuccessfulLogin();

            if (res.operator.role === 'ADMIN_GERAL') {
                await this.context.login('ADMIN_GERAL', 'GLOBAL', res.operator);
                this.toast.show(`Bem-vindo à Gestão Global, ${res.operator.name}!`, 'success');
            } else if (res.operator.role === 'ADMIN_MUNICIPAL') {
                await this.context.login('ADMIN_MUNICIPAL', res.municipality.id, res.operator);
                this.toast.show(`Acesso Municipal: Bem-vindo, ${res.operator.name}!`, 'success');
            } else {
                await this.context.login(res.operator.role, res.municipality.id, res.operator);
                this.toast.show(`Bem-vindo, ${res.operator.name}!`, 'success');
            }
        } catch (err: unknown) {
            // Limpa o campo de senha para evitar cliques sucessivos e bloqueio acidental do terminal
            this.passwordInput = '';
            this.showPassword.set(false);

            // Registra falha de tentativa e aplica bloqueio de força bruta se atingir limite
            const lockStatus = this.sessionSecurity.recordFailedAttempt(this.loginInput);

            const errorMessage = err instanceof Error ? err.message : String(err || '');
            const msg = errorMessage.toLowerCase();
            if (lockStatus.isLocked) {
                this.toast.show(`Acesso bloqueado por 5 minutos devido a 5 tentativas incorretas consecutivas.`, 'error');
            } else if (msg.includes('cannot read') && msg.includes('operator')) {
                this.toast.show('Erro ao carregar dados do usuário. Faça login novamente.', 'error');
            } else {
                this.toast.show(`${errorMessage || 'Login ou senha incorreto.'} Restam ${lockStatus.attemptsLeft} tentativa(s).`, 'error');
            }
        } finally {
            this.isLoggingIn.set(false);
        }
    }
}

